import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { motion } from "motion/react";
import * as React from "react";

import { cn } from "../../utils/cn";
import { easings } from "../../utils/easings";

import { type ButtonInteraction, buttonVariants } from "./button";

/**
 * TabsMenu — horizontal/vertical tab container with a parent-owned sliding
 * indicator. Composes:
 *   - `<TabsMenu value={v} onValueChange={setV}>` (root + list)
 *   - `<TabTrigger value="X">Label</TabTrigger>` (styled Base UI tab)
 *
 * The indicator bar lives in TabsMenu and animates between active tab
 * positions (motion/react, x + width with per-property easeInOut).
 *
 * Hover fill for each tab comes from the Button v3 `tab` variant +
 * `fillFromBottom` (or `-Top`) interaction on TabTrigger. Indicator position
 * (`top` / `bottom`) is independent of the fill direction — that's a
 * deliberate knob on TabTrigger, not driven by TabsMenu.
 *
 * Pair with TabsContent (re-exported from ./tabs) for panel bodies:
 *   <TabsMenu value><TabTrigger value="a">A</TabTrigger></TabsMenu>
 *   <TabsContent value="a">…</TabsContent>
 */

interface TabsMenuProps {
    value: string;
    onValueChange?: (value: string) => void;
    /** Where the sliding indicator bar sits relative to the tab row. */
    indicatorPosition?: "top" | "bottom";
    /** Optional full-width baseline (1px line-base) at the top or bottom
     *  of the tab row. The sliding indicator sits on top of its slice —
     *  baseline shows through the rest of the row. */
    border?: "none" | "top" | "bottom";
    orientation?: "horizontal" | "vertical";
    align?: "start" | "center";
    /** Stretch the list to fill the parent width. Default compact (hug content). */
    fullWidth?: boolean;
    className?: string;
    children?: React.ReactNode;
}

function TabsMenu({
    value,
    onValueChange,
    indicatorPosition = "bottom",
    border = "none",
    orientation = "horizontal",
    align = "center",
    fullWidth = false,
    className,
    children
}: TabsMenuProps): React.ReactElement {
    const listRef = React.useRef<HTMLDivElement>(null);
    const [rect, setRect] = React.useState<{ x: number; width: number } | null>(
        null
    );

    const measureActive = React.useCallback(() => {
        if (!listRef.current) return;
        const activeTab = listRef.current.querySelector<HTMLElement>(
            `[role="tab"][data-active], [role="tab"][aria-selected="true"]`
        );
        if (!activeTab) {
            setRect(null);
            return;
        }
        const listBox = listRef.current.getBoundingClientRect();
        const tabBox = activeTab.getBoundingClientRect();
        setRect({
            x: tabBox.left - listBox.left,
            width: tabBox.width
        });
    }, []);

    React.useLayoutEffect(() => {
        measureActive();
    }, [measureActive, value]);

    React.useEffect(() => {
        window.addEventListener("resize", measureActive);
        return (): void => window.removeEventListener("resize", measureActive);
    }, [measureActive]);

    return (
        <TabsPrimitive.Root
            value={value}
            onValueChange={(v) => onValueChange?.(v as string)}
            orientation={orientation}
        >
            <TabsPrimitive.List
                ref={listRef as unknown as React.Ref<HTMLDivElement>}
                data-slot="tabs-menu"
                data-indicator-position={indicatorPosition}
                className={cn(
                    "relative inline-flex items-stretch gap-0",
                    orientation === "vertical" && "flex-col items-stretch",
                    align === "start" ? "justify-start" : "justify-center",
                    fullWidth && "w-full",
                    className
                )}
            >
                {children}
                {border !== "none" && (
                    <span
                        aria-hidden
                        className={cn(
                            "pointer-events-none absolute inset-x-0 h-px bg-line-base",
                            border === "top" ? "top-0" : "bottom-0"
                        )}
                    />
                )}
                {rect && (
                    <motion.span
                        aria-hidden
                        className={cn(
                            "pointer-events-none absolute left-0 h-[2px] z-10 bg-accent-base",
                            indicatorPosition === "top" ? "top-0" : "bottom-0"
                        )}
                        initial={false}
                        animate={{ x: rect.x, width: rect.width }}
                        transition={{
                            x: { duration: 0.5, ease: easings.outExpo },
                            width: { duration: 0.4, ease: easings.outExpo }
                        }}
                    />
                )}
            </TabsPrimitive.List>
        </TabsPrimitive.Root>
    );
}

interface TabTriggerProps {
    value: string;
    children?: React.ReactNode;
    className?: string;
    size?: "xs" | "fit" | "sm" | "md" | "lg";
    /** Fill animation direction. Default `fillFromBottom` (bar fills upward). */
    interaction?: Extract<ButtonInteraction, "fillFromTop" | "fillFromBottom">;
    disabled?: boolean;
}

function TabTrigger({
    value,
    children,
    className,
    size = "md",
    interaction = "fillFromBottom",
    disabled
}: TabTriggerProps): React.ReactElement {
    // Reuse Button v3's `tab` variant class chain — fg/bg/line per state.
    // Fill span is rendered locally (Button's fill render branch is
    // ButtonPrimitive-specific; TabTrigger needs TabsPrimitive.Tab).
    const baseClass = buttonVariants({
        variant: "tab",
        interaction,
        size,
        outline: false,
        fullWidth: false
    });
    const fillAnchor = interaction === "fillFromTop" ? "top-0" : "bottom-0";

    return (
        <TabsPrimitive.Tab
            value={value}
            disabled={disabled}
            data-slot="tab-trigger"
            className={cn(
                "group relative overflow-hidden",
                baseClass,
                className
            )}
        >
            <span
                className={cn(
                    "pointer-events-none absolute inset-x-0 h-0 z-0 transition-[height] duration-300 ease-out bg-button-tab-surface-focus",
                    fillAnchor,
                    !disabled && "group-hover:h-full"
                )}
            />
            <span className="relative z-20 inline-flex items-center gap-2">
                {children}
            </span>
        </TabsPrimitive.Tab>
    );
}

export { TabsMenu, TabTrigger };
export type { TabsMenuProps, TabTriggerProps };
